import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from 'react-plaid-link';
import { version as plaidSdkVersion } from 'react-plaid-link/package.json';

import {
  PlaidIntentTypes,
  PlaidV2SharedCallbacks,
  WebOnResolve,
} from './types';

const usePlaidV2Platform = (
  _intent: PlaidIntentTypes, // intent is not used in the web implementation, but we keep it as a parameter for consistency with the native implementation.
  token: string | null,
  callbacks: PlaidV2SharedCallbacks,
) => {
  const openResolveRef = useRef<((result: WebOnResolve) => void) | null>(null);
  const canOpenRef = useRef(false);
  const onSuccessRef = useRef(callbacks.onSuccess);
  const onExitRef = useRef(callbacks.onExit);
  const onEventRef = useRef(callbacks.onEvent);

  onSuccessRef.current = callbacks.onSuccess;
  onExitRef.current = callbacks.onExit;
  onEventRef.current = callbacks.onEvent;

  const config: PlaidLinkOptions = useMemo(() => {
    return {
      token: token,
      onSuccess: (
        publicToken: string,
        metadata: PlaidLinkOnSuccessMetadata,
      ) => {
        const resolve = openResolveRef.current;
        if (!resolve) {
          return;
        }

        openResolveRef.current = null;

        void (async () => {
          try {
            const result = await onSuccessRef.current({
              publicToken,
              metadata: {
                linkSessionId: metadata.link_session_id,
                institutionId: metadata.institution?.institution_id,
                institutionName: metadata.institution?.name,
                accounts: metadata.accounts,
              },
              sdkVersion: plaidSdkVersion,
            });
            if (result !== 'success') {
              resolve({ status: 'error', error: result });
              return;
            }
            resolve({ status: 'success', success: { publicToken, metadata } });
          } catch (error) {
            resolve({
              status: 'error',
              error: {
                name: error instanceof Error ? error.name : 'UnknownError',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Unexpected Plaid success callback error',
              },
            });
          }
        })();
      },
      onExit: (
        error: PlaidLinkError | null,
        metadata: PlaidLinkOnExitMetadata,
      ) => {
        const resolve = openResolveRef.current;

        if (!resolve) {
          return;
        }

        openResolveRef.current = null;

        const exitResult = onExitRef.current({
          metadata: {
            linkSessionId: metadata.link_session_id,
            requestId: metadata.request_id,
            institutionId: metadata.institution?.institution_id,
            institutionName: metadata.institution?.name,
            exitStatus: metadata.status ?? undefined,
          },
          errorCode: error?.error_code,
          errorType: error?.error_type,
          errorMessage: error?.error_message,
          displayMessage: error?.display_message ?? undefined,
        });

        if (exitResult) {
          resolve({ status: 'error', error: exitResult });
          return;
        }

        resolve({
          status: 'exit',
          exit: { error, metadata },
        });
      },
      onEvent: (eventName, metadata) => onEventRef.current(eventName, metadata),
    };
  }, [token]);

  const { open, ready, error: sdkError } = usePlaidLink(config);

  // `usePlaidLink` doesn't reset its internal Plaid instance state when the
  // token changes — it just destroys the old handle inside its useEffect and
  // creates a new one. Between the render that receives the new token and the
  // effect that installs the new instance, `ready` and `open` still refer to
  // the destroyed instance. Calling `open()` in that window is a silent no-op
  // and the Plaid iframe never mounts. Track the token that the current `open`
  // reference was committed for and only report ready once they line up.
  const [committedToken, setCommittedToken] = useState<string | null>(null);
  useEffect(() => {
    setCommittedToken(token);
  }, [token, open]);

  // Only consider ready when we actually have a token, no SDK error, and
  // usePlaidLink has committed a new Plaid instance for the current token.
  const isReady = ready && !!token && !sdkError && committedToken === token;
  canOpenRef.current = isReady;
  const isReadyRef = useRef(isReady);
  isReadyRef.current = isReady;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const openRef = useRef(open);
  openRef.current = open;

  // If the Plaid SDK surfaces an error (e.g. script load failure) while a
  // promise is pending, resolve it immediately rather than leaving it hanging.
  useEffect(() => {
    if (!sdkError) {
      return;
    }

    const resolve = openResolveRef.current;
    if (!resolve) {
      return;
    }

    resolve({
      status: 'error',
      error: {
        name: 'PlaidSdkError',
        message: sdkError.message,
      },
    });
    openResolveRef.current = null;
  }, [sdkError]);

  /**
   * Stable callback identity — uses refs internally so the function
   * reference doesn't change when token/callbacks update.
   */
  const openPlaid = useCallback((): Promise<WebOnResolve> => {
    if (!tokenRef.current) {
      return Promise.resolve({ status: 'blocked', reason: 'missing-token' });
    }

    if (!canOpenRef.current) {
      return Promise.resolve({ status: 'blocked', reason: 'sdk-not-ready' });
    }

    return new Promise<WebOnResolve>((resolve) => {
      openResolveRef.current = resolve;
      openRef.current();
    });
  }, []);

  const reset = useCallback(() => {
    openResolveRef.current = null;
  }, []);

  return {
    openPlaid,
    ready: isReady,
    reset,
  };
};

export default usePlaidV2Platform;
